/*
🔍 Threat Detail Modal Component
Safety Intelligence - Tehdit Detay Görüntüleme
*/

import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Timeline,
  Card,
  Row,
  Col,
  Progress,
  Statistic,
  Button,
  Space,
  Typography,
  Alert,
  Divider,
  List,
  Avatar,
  Tooltip
} from 'antd';
import {
  WarningOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  ReloadOutlined,
  PrinterOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

interface ThreatReport {
  id: string;
  timestamp: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  location: string;
  description: string;
  status: 'detected' | 'investigating' | 'resolved' | 'false_positive';
  confidence: number;
  responseTime: number;
  coordinates?: {
    lat: number;
    lng: number;
  };
  involvedPersons?: number;
  reportedBy?: string;
  assignedTo?: string;
  timeline?: Array<{
    time: string;
    action: string;
    actor: string;
    status: string;
  }>;
  relatedReports?: string[];
  evidences?: Array<{
    type: string;
    description: string;
    source: string;
  }>;
  riskAssessment?: {
    publicSafety: number;
    economicImpact: number;
    socialImpact: number;
    recurringRisk: number;
  };
}

interface ThreatDetailModalProps {
  visible: boolean;
  onClose: () => void;
  threatData: ThreatReport | null;
}

export const ThreatDetailModal: React.FC<ThreatDetailModalProps> = ({
  visible,
  onClose,
  threatData
}) => {
  const { isDarkMode } = useTheme();

  if (!threatData) return null;

  // Severity renk mapping
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  // Status renk mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'green';
      case 'investigating': return 'orange';
      case 'detected': return 'blue';
      case 'false_positive': return 'red';
      default: return 'default';
    }
  };

  // Status Türkçe mapping
  const getStatusText = (status: string) => {
    switch (status) {
      case 'detected': return 'Tespit Edildi';
      case 'investigating': return 'İnceleniyor';
      case 'resolved': return 'Çözüldü';
      case 'false_positive': return 'Yanlış Alarm';
      default: return status;
    }
  };

  // Mock data completion for demo
  const completeData: ThreatReport = {
    ...threatData,
    coordinates: threatData.coordinates || { lat: 41.0082, lng: 28.9784 },
    involvedPersons: threatData.involvedPersons || Math.floor(Math.random() * 10) + 1,
    reportedBy: threatData.reportedBy || 'Otomatik Sistem',
    assignedTo: threatData.assignedTo || 'Güvenlik Ekibi A',
    timeline: threatData.timeline || [
      {
        time: dayjs(threatData.timestamp).format('DD.MM.YYYY HH:mm'),
        action: 'Tehdit tespit edildi',
        actor: 'AI Sistem',
        status: 'detected'
      },
      {
        time: dayjs(threatData.timestamp).add(5, 'minute').format('DD.MM.YYYY HH:mm'),
        action: 'Güvenlik ekibine bildirildi',
        actor: 'Otomatik Sistem',
        status: 'notified'
      },
      {
        time: dayjs(threatData.timestamp).add(threatData.responseTime, 'minute').format('DD.MM.YYYY HH:mm'),
        action: threatData.status === 'resolved' ? 'Olay çözüldü' : 'İnceleme devam ediyor',
        actor: threatData.assignedTo || 'Güvenlik Ekibi',
        status: threatData.status
      }
    ],
    relatedReports: threatData.relatedReports || ['TR-2024-001', 'TR-2024-003'],
    evidences: threatData.evidences || [
      {
        type: 'Video Kaydı',
        description: 'Güvenlik kamerası kayıtları',
        source: 'CCTV Sistem'
      },
      {
        type: 'Tanık Beyanı',
        description: '2 tanık ifadesi alındı',
        source: 'Güvenlik Personeli'
      }
    ],
    riskAssessment: threatData.riskAssessment || {
      publicSafety: 75,
      economicImpact: 40,
      socialImpact: 60,
      recurringRisk: 30
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span>Tehdit Analizi Detayları</span>
          <Tag color={getSeverityColor(completeData.severity)}>
            {completeData.severity.toUpperCase()}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={
        <Space>
          <Button icon={<PrinterOutlined />}>
            Yazdır
          </Button>
          <Button icon={<ShareAltOutlined />}>
            Paylaş
          </Button>
          <Button icon={<ReloadOutlined />}>
            Yenile
          </Button>
          <Button type="primary" onClick={onClose}>
            Kapat
          </Button>
        </Space>
      }
      style={{ top: 20 }}
    >
      <Row gutter={[24, 24]}>
        {/* Temel Bilgiler */}
        <Col span={24}>
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                <span>Temel Bilgiler</span>
              </Space>
            }
            size="small"
          >
            <Descriptions column={3} size="small">
              <Descriptions.Item label="Rapor ID">
                <Text code>{completeData.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tarih/Saat">
                {dayjs(completeData.timestamp).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(completeData.status)}>
                  {getStatusText(completeData.status)}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="Tehdit Türü">
                {completeData.type}
              </Descriptions.Item>
              <Descriptions.Item label="Önem Seviyesi">
                <Tag color={getSeverityColor(completeData.severity)}>
                  {completeData.severity.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Güven Skoru">
                <Progress 
                  percent={completeData.confidence} 
                  size="small" 
                  status={completeData.confidence > 80 ? 'success' : completeData.confidence > 60 ? 'active' : 'exception'}
                />
              </Descriptions.Item>
              
              <Descriptions.Item label="Konum">
                <Space>
                  <EnvironmentOutlined />
                  {completeData.location}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Müdahale Süresi">
                <Space>
                  <ClockCircleOutlined />
                  {completeData.responseTime} dakika
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="İlgili Kişi Sayısı">
                <Space>
                  <UserOutlined />
                  {completeData.involvedPersons} kişi
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Açıklama ve Risk Değerlendirmesi */}
        <Col span={16}>
          <Card 
            title="Olay Açıklaması" 
            size="small"
            style={{ height: '100%' }}
          >
            <Paragraph>{completeData.description}</Paragraph>
            
            <Divider />
            
            <Title level={5}>Koordinatlar:</Title>
            <Text type="secondary">
              Enlem: {completeData.coordinates.lat}, Boylam: {completeData.coordinates.lng}
            </Text>
            
            <Divider />
            
            <Title level={5}>Raporlayan:</Title>
            <Space>
              <Avatar icon={<UserOutlined />} size="small" />
              <Text>{completeData.reportedBy}</Text>
            </Space>
            
            <Divider />
            
            <Title level={5}>Sorumlu Ekip:</Title>
            <Space>
              <Avatar icon={<SafetyCertificateOutlined />} size="small" />
              <Text>{completeData.assignedTo}</Text>
            </Space>
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Risk Değerlendirmesi" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Kamu Güvenliği</Text>
                <Progress 
                  percent={completeData.riskAssessment.publicSafety} 
                  size="small"
                  strokeColor={completeData.riskAssessment.publicSafety > 70 ? '#ff4d4f' : '#faad14'}
                />
              </div>
              
              <div>
                <Text strong>Ekonomik Etki</Text>
                <Progress 
                  percent={completeData.riskAssessment.economicImpact} 
                  size="small"
                  strokeColor="#1890ff"
                />
              </div>
              
              <div>
                <Text strong>Sosyal Etki</Text>
                <Progress 
                  percent={completeData.riskAssessment.socialImpact} 
                  size="small"
                  strokeColor="#52c41a"
                />
              </div>
              
              <div>
                <Text strong>Tekrar Riski</Text>
                <Progress 
                  percent={completeData.riskAssessment.recurringRisk} 
                  size="small"
                  strokeColor="#fa8c16"
                />
              </div>
            </Space>
          </Card>
        </Col>

        {/* Zaman Çizelgesi */}
        <Col span={12}>
          <Card title="Olay Zaman Çizelgesi" size="small">
            <Timeline size="small">
              {completeData.timeline.map((item, index) => (
                <Timeline.Item
                  key={index}
                  dot={
                    item.status === 'resolved' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                    item.status === 'detected' ? <ExclamationCircleOutlined style={{ color: '#faad14' }} /> :
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  }
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{item.action}</Text>
                    <Text type="secondary">{item.actor} - {item.time}</Text>
                  </Space>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>

        {/* Kanıtlar ve İlgili Raporlar */}
        <Col span={12}>
          <Card title="Kanıtlar ve Belgeler" size="small">
            <List
              size="small"
              dataSource={completeData.evidences}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<FileTextOutlined />} size="small" />}
                    title={item.type}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text>{item.description}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Kaynak: {item.source}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            
            <Divider />
            
            <Title level={5}>İlgili Raporlar:</Title>
            <Space wrap>
              {completeData.relatedReports.map((reportId) => (
                <Tooltip key={reportId} title="İlgili raporu aç">
                  <Tag 
                    color="blue" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => console.log(`Opening report: ${reportId}`)}
                  >
                    {reportId}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </Card>
        </Col>

        {/* Durum Güncellemesi */}
        <Col span={24}>
          {completeData.status === 'investigating' && (
            <Alert
              message="Olay İnceleme Aşamasında"
              description="Bu olay şu anda güvenlik ekibi tarafından inceleniyor. Gelişmeler sistem üzerinden takip edilebilir."
              type="info"
              icon={<ClockCircleOutlined />}
              showIcon
            />
          )}
          
          {completeData.status === 'resolved' && (
            <Alert
              message="Olay Başarıyla Çözüldü"
              description={`Bu olay ${completeData.responseTime} dakika içinde başarıyla çözüldü. Tüm güvenlik protokolleri uygulandı.`}
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
            />
          )}
          
          {completeData.status === 'false_positive' && (
            <Alert
              message="Yanlış Alarm Tespit Edildi"
              description="Bu alarm sistemin yanlış pozitif tespiti olarak değerlendirildi. AI modeli bu tür durumları önlemek için güncellenecek."
              type="warning"
              icon={<WarningOutlined />}
              showIcon
            />
          )}
        </Col>
      </Row>
    </Modal>
  );
};
